import {
    areSearchParamsEqual,
    QUEUE_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type QueueFilterItem = { key: string; value: string };

export type QueueUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterCampaignId: QueueFilterItem | null;
    filterCampaignName: QueueFilterItem | null;
    filterCampaignTypeId: QueueFilterItem[] | null;
    filterStatusId: QueueFilterItem[] | null;
    filterBranchId: QueueFilterItem[] | null;
    filterQueueType: QueueFilterItem[] | null;
    filterQueueActive: QueueFilterItem[] | null;
    uiFilters: Record<string, any>;
};

export type QueueUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterCampaignId: QueueFilterItem | null;
    filterCampaignName: QueueFilterItem | null;
    filterCampaignTypeId: QueueFilterItem[] | null;
    filterStatusId: QueueFilterItem[] | null;
    filterBranchId: QueueFilterItem[] | null;
    filterQueueType: QueueFilterItem[] | null;
    filterQueueActive: QueueFilterItem[] | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialQueueUrlState(): QueueUrlViewState {
    return parseQueueUrlState(getHashSearchParams());
}

export function parseQueueUrlState(params: URLSearchParams): QueueUrlViewState {
    params = unwrapEncodedSearchParams(params, QUEUE_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const archiveTableToggle = !parseBooleanParam(params.get('archived'), false);

    const id = params.get('id');
    const name = params.get('name');
    const isQueueTrunk = getMultiParams(params, 'isQueueTrunk').map(toBooleanValue);
    const priority = getMultiParams(params, 'priority').map(toNumberValue);
    const isActive = getMultiParams(params, 'isActive').map(toBooleanValue);
    const branchId = getMultiParams(params, 'branchId').map(toNumberValue);
    const queueType = getMultiParams(params, 'queueType').map(toNumberValue);

    if (id) uiFilters.id = id;
    if (name) uiFilters.name = name;
    if (isQueueTrunk.length) uiFilters.isGeneralTrunk = isQueueTrunk;
    if (priority.length) uiFilters.priority = priority;
    if (isActive.length) uiFilters.isActive = isActive;
    if (branchId.length) uiFilters.branchId = branchId;
    if (queueType.length) uiFilters.queueType = queueType;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        archiveTableToggle,
        filterCampaignId: toFilterItem('Id', id),
        filterCampaignName: toFilterItem('Name', name),
        filterCampaignTypeId: toFilterList('IsQueueTrunk', isQueueTrunk),
        filterStatusId: toFilterList('Priority', priority),
        filterBranchId: toFilterList('BranchId', branchId),
        filterQueueType: toFilterList('QueueType', queueType),
        filterQueueActive: toFilterList('IsActive', isActive),
        uiFilters,
    };
}

export function buildQueueSearchParams(state: QueueUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainQueueSearchParams(state), QUEUE_ENCODED_QUERY_KEY);
}

function buildPlainQueueSearchParams(state: QueueUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (!state.archiveTableToggle) {
        params.set('archived', '1');
    }

    setScalar(params, 'id', state.filterCampaignId?.value);
    setScalar(params, 'name', state.filterCampaignName?.value);
    setMulti(params, 'isQueueTrunk', state.filterCampaignTypeId);
    setMulti(params, 'priority', state.filterStatusId);
    setMulti(params, 'isActive', state.filterQueueActive);
    setMulti(params, 'branchId', state.filterBranchId);
    setMulti(params, 'queueType', state.filterQueueType);

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: QueueFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value !== undefined && item?.value !== null && item.value !== '') {
            params.append(key, normalizeBooleanParam(item.value));
        }
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): QueueFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number | boolean>): QueueFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function toBooleanValue(value: string): boolean {
    return value === '1' || value === 'true';
}

function toNumberValue(value: string): number | string {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function normalizeBooleanParam(value: string): string {
    if (value === 'true') return '1';
    if (value === 'false') return '0';
    return value;
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
